import { PagePagination } from '@components/core-ui/Pagination';
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Tag } from '@components/core-ui/Tag';
import { Input } from '@components/core-ui/form-elements/Input';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import SearchIcon from '@components/icons/Search.svg';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createFetchAsrJobsQueryFn } from '../../api/fetchAsrJobs';
import { getStatusTagClassName } from '../../helpers/getStatusTagClassName';
import { formatJobMetadataValue } from './helpers/formatJobMetadataValue';
import { useAsrProxyConsoleContext } from '../../providers/AsrProxyConsoleProvider';

const REFRESH_INTERVAL_MS = 4000;
const SEARCH_DEBOUNCE_MS = 300;

export const JobsTab = () => {
  const { call } = useAPI();
  const { openInPlayground, loadingJobId, jobsSearch: search, setJobsSearch: setSearch } = useAsrProxyConsoleContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;

  const jobsQuery = useQuery({
    queryKey: asrProxyKeys.jobs(debouncedSearch),
    queryFn: createFetchAsrJobsQueryFn(call),
    refetchInterval: REFRESH_INTERVAL_MS,
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });
  const jobs = jobsQuery.data?.jobs ?? [];
  const isLoading = jobsQuery.isFetching || isSearchSettling;

  const start = (page - 1) * pageSize;
  const slice = jobs.slice(start, start + pageSize);

  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">Recent jobs</h2>
          <p className="mt-1 text-sm text-foreground-muted">Updated automatically every four seconds.</p>
        </div>
        <div className="min-w-[280px] max-w-[420px] flex-1">
          <Input
            name="jobs-search"
            icon={SearchIcon}
            label="Search proxy id, provider id, pipelineId, jobId"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <OutlinedButton className="!w-auto" onClick={() => void jobsQuery.refetch()}>
          Refresh
        </OutlinedButton>
      </div>
      <LinearProgressLoader active={isLoading || loadingJobId !== null} />
      <TableWrapper>
        <Table>
          <Thead $white>
            <Tr>
              <Th>Proxy id</Th>
              <Th>Status</Th>
              <Th>Model</Th>
              <Th>Provider</Th>
              <Th>Attempts</Th>
              <Th>Provider id</Th>
              <Th>Metadata</Th>
              <Th>Error</Th>
              <Th>Updated</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {slice.map((job) => (
              <Tr key={job.id}>
                <Td>
                  <code className="rounded bg-page px-1.5 py-0.5 text-xs text-foreground">{job.id}</code>
                </Td>
                <Td>
                  <Tag text={job.status} className={getStatusTagClassName(job.status)} />
                </Td>
                <Td>{job.model}</Td>
                <Td>{job.provider}</Td>
                <Td className="!whitespace-normal">
                  {job.attempts && job.attempts.length ? (
                    job.attempts.map((attempt, index) => (
                      <span key={`${attempt.model}-${index}`} title={attempt.error ?? ''}>
                        {index > 0 ? <span className="text-foreground-muted"> → </span> : null}
                        {attempt.model}
                        <span className={attempt.ok ? 'text-[var(--color-green)]' : 'text-[var(--color-danger)]'}>
                          {attempt.ok ? ' ✓' : ' ✗'}
                        </span>
                        {attempt.tries > 1 ? <span className="text-foreground-muted">×{attempt.tries}</span> : null}
                      </span>
                    ))
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </Td>
                <Td>
                  <code className="rounded bg-page px-1.5 py-0.5 text-xs text-foreground">
                    {job.provider_transcript_id ?? '—'}
                  </code>
                </Td>
                <Td className="!whitespace-normal">
                  {job.metadata && Object.keys(job.metadata).length ? (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(job.metadata).map(([key, value]) => {
                        const formattedValue = formatJobMetadataValue(value);

                        return (
                          <span
                            key={key}
                            className="inline-block rounded border border-border bg-page px-1.5 py-0.5 text-[11px]"
                          >
                            <b className="font-semibold text-foreground-muted">{key}</b>=
                            {key === 'jobId' && formattedValue ? (
                              <Link className="text-accent underline" to={routes.adminJobDetails.make(formattedValue)}>
                                {formattedValue}
                              </Link>
                            ) : (
                              formattedValue
                            )}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </Td>
                <Td className="max-w-[280px] !whitespace-normal">
                  {job.error ? (
                    <div className="text-xs" title={job.error}>
                      {job.failed_stage ? (
                        <code className="mr-1 rounded bg-page px-1.5 py-0.5 text-[11px] text-danger">
                          {job.failed_stage}
                        </code>
                      ) : null}
                      <span className="line-clamp-2 break-words text-danger [overflow-wrap:anywhere]">{job.error}</span>
                    </div>
                  ) : (
                    <span className="text-foreground-muted">—</span>
                  )}
                </Td>
                <Td className="text-foreground-muted">{new Date(job.updated_at).toLocaleTimeString()}</Td>
                <Td>
                  {job.has_transcript || job.has_execution ? (
                    <TextButton
                      className="!w-auto"
                      disabled={loadingJobId !== null}
                      onClick={() => openInPlayground(job)}
                    >
                      {job.has_transcript ? 'Open' : 'Inspect'}
                    </TextButton>
                  ) : null}
                </Td>
              </Tr>
            ))}
            {jobs.length === 0 ? (
              <Tr>
                <Td className="text-foreground-muted" colSpan={10}>
                  {search.trim() ? 'No matching jobs.' : 'No jobs yet.'}
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </TableWrapper>
      <PagePagination
        totalItemsCount={jobs.length}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </section>
  );
};
